/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InfoCardKernelType, UnifiedCollectionInfoCardKernel } from 'client-code/actions';
import { FeedItemType, OfferType } from 'client-code/wrs-feed-service';
import { HomeScreenInteractor } from 'src/core/interactors/HomeScreenInteractor';
import { NetworkManager } from 'src/core/network/NetworkManager';
import { showNoNetworkOverlay } from 'src/store/overlay/nonetworkoverlay/nonetworkoverlay.reducers';

import { DeviceModel } from '../../../core/device/DeviceModel';
import { ContentType } from '../../../core/enums/ContentType';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import { InfoCardManager } from '../../../core/infocard';
import { Log } from '../../../core/logging/Log';
import { KeyManager } from '../../../core/navigation';
import { PlaybackContentType } from '../../../core/playback/enums';
import { SharedPrefConstants } from '../../../core/storage';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { isHomeScreenCcbSwitchEnabled } from '../../../core/utils/ccb/CcbSwitchHelper';
import { IPlayable } from '../../../core/videoplayer/interfaces/IPlayable';
import { clearActiveInteractiveComponent } from '../../interactivelayer/interactiveLayer.reducers';
import { showExitConfirmationOverlayDialog } from '../../overlay/exitconfirmationoverlay/exitconfirmationoverlay.reducers';
import { showRecordOverlay } from '../../overlay/recordoverlay/recordoverlay.reducers';
import {
    collectionScreenTransition,
    programScreenTransition,
} from '../../screens/navigation/screensnavigation';
import {
    showGuideScreen,
    showMainSettingsScreen,
    showMyShowsScreen,
    showVodBrowseMainScreen,
} from '../../screens/screens.reducers';
import { showSidebarNavigationMenu } from '../../sidebarnavigationmenu/sidebarnavigationmenu.reducers';
import { startVideoPlayback } from '../../videoplayer/videoplayer.reducers';
import {
    setHomeScreenFocusAction,
    setHomeScreenGhostAction,
    setHomeScreenNavFocusAction,
    setHomeScreenPreviewAction,
    setHomeScreenVerticalScrollAction,
} from '../homescreen.actions';
import {
    checkAndLoadMoreCarousels,
    checkAndLoadMoreFeedItems,
    fetchPreviewForFocusedTile,
    getSingleChannelInfo,
    startNdvrPlayback,
    startVodPlayback,
} from '../homescreen.reducers';

const TAG = 'homeScreenKeyOperations';

const clearGhost = { carouselIndex: null, tileIndex: null, navBarIndex: null, scrollOffset: 0 };

/**
 * Dismiss all mouse-related UI (ghost highlight, carets, cached scroll state).
 * Called at the start of every key handler to ensure a clean switch from
 * mouse to keyboard input mode.
 */
const dismissMouseState = (dispatch: Function): void => {
    dispatch(setHomeScreenGhostAction(clearGhost));
    dispatch(clearActiveInteractiveComponent());
};

const focusCarouselAtIndex = (
    dispatch: Function,
    targetIndex: number,
    carousels: any[],
    tileIndexPerCarousel: Record<number, number>,
) => {
    const tileCount = carousels[targetIndex]?.feedItems?.feedItems?.length ?? 0;
    const savedTileIndex = tileIndexPerCarousel[targetIndex] ?? 0;
    const tileIndex = Math.min(savedTileIndex, Math.max(0, tileCount - 1));
    dismissMouseState(dispatch);
    dispatch(
        setHomeScreenFocusAction({
            carouselIndex: targetIndex,
            tileIndex,
        }),
    );
    dispatch(setHomeScreenPreviewAction(null));
    dispatch(fetchPreviewForFocusedTile());
};

export const onKeyRightHomeScreenOperations = () => (dispatch: Function, getState: TGetState) => {
    const { homeScreen } = getState();

    if (homeScreen.navBarFocused) {
        const navItemCount = homeScreen.navItems.length + 1; // +1 for Settings
        if (homeScreen.navBarIndex < navItemCount - 1) {
            dispatch(setHomeScreenNavFocusAction(true, homeScreen.navBarIndex + 1));
        }
        return;
    }

    const { focusState, carousels } = homeScreen;
    const carousel = carousels[focusState.carouselIndex];
    if (!carousel) return;

    const tileCount = carousel.feedItems?.feedItems?.length ?? 0;
    const maxIndex = tileCount - 1;

    if (focusState.tileIndex < maxIndex) {
        dismissMouseState(dispatch);
        dispatch(
            setHomeScreenFocusAction({
                carouselIndex: focusState.carouselIndex,
                tileIndex: focusState.tileIndex + 1,
            }),
        );
        dispatch(setHomeScreenPreviewAction(null));
        dispatch(fetchPreviewForFocusedTile());
        dispatch(checkAndLoadMoreFeedItems());
    } else {
        // Already at the last loaded tile — still try to fetch more items
        // so the next right-press can proceed once they've loaded.
        dispatch(checkAndLoadMoreFeedItems());
    }
};

export const onKeyLeftHomeScreenOperations = () => (dispatch: Function, getState: TGetState) => {
    const { homeScreen } = getState();

    if (homeScreen.navBarFocused) {
        if (homeScreen.navBarIndex > 0) {
            dispatch(setHomeScreenNavFocusAction(true, homeScreen.navBarIndex - 1));
        }
        return;
    }

    const { focusState } = homeScreen;

    if (focusState.tileIndex > 0) {
        dismissMouseState(dispatch);
        dispatch(
            setHomeScreenFocusAction({
                carouselIndex: focusState.carouselIndex,
                tileIndex: focusState.tileIndex - 1,
            }),
        );
        dispatch(setHomeScreenPreviewAction(null));
        dispatch(fetchPreviewForFocusedTile());
    }
};

export const onKeyUpHomeScreenOperations = () => (dispatch: Function, getState: TGetState) => {
    const { homeScreen } = getState();

    if (homeScreen.navBarFocused) {
        // Already at top — no action
        return;
    }

    const { focusState, carousels, tileIndexPerCarousel, verticalScrollIndex, pageCursor } =
        homeScreen;

    if (focusState.carouselIndex > 0) {
        const isAtLastCarousel = focusState.carouselIndex === carousels.length - 1;
        const isLastPage = !pageCursor;
        const highlightIsAhead =
            isLastPage && isAtLastCarousel && verticalScrollIndex < focusState.carouselIndex;

        if (highlightIsAhead) {
            // Edge case: highlight was moved down to the last carousel.
            // Move it back up to the second-to-last WITHOUT shifting the list.
            focusCarouselAtIndex(
                dispatch,
                focusState.carouselIndex - 1,
                carousels,
                tileIndexPerCarousel,
            );
        } else {
            // Normal case: shift the carousel list down (decrement verticalScrollIndex)
            // and move focus up together.
            const newScrollIndex = Math.max(0, verticalScrollIndex - 1);
            dispatch(setHomeScreenVerticalScrollAction(newScrollIndex));
            focusCarouselAtIndex(
                dispatch,
                focusState.carouselIndex - 1,
                carousels,
                tileIndexPerCarousel,
            );
        }
    } else {
        // Move focus to nav bar
        dismissMouseState(dispatch);
        // Move focus to nav bar — always land on Guide (index 0)
        dispatch(setHomeScreenNavFocusAction(true, 0));
        dispatch(setHomeScreenPreviewAction(null));
    }
};

export const onKeyDownHomeScreenOperations = () => (dispatch: Function, getState: TGetState) => {
    const { homeScreen } = getState();

    const { focusState, carousels, tileIndexPerCarousel, verticalScrollIndex, pageCursor } =
        homeScreen;

    if (homeScreen.navBarFocused) {
        // Return focus to carousel area
        if (carousels.length === 0) {
            dispatch(setHomeScreenNavFocusAction(true, homeScreen.navBarIndex));
            return;
        }
        dispatch(setHomeScreenNavFocusAction(false, homeScreen.navBarIndex));
        dispatch(fetchPreviewForFocusedTile());
        return;
    }

    if (focusState.carouselIndex < carousels.length - 1) {
        const isLastPage = !pageCursor;
        const isAtSecondToLast = focusState.carouselIndex === carousels.length - 2;

        if (isLastPage && isAtSecondToLast) {
            // Edge case: last page of data and currently at the second-to-last carousel.
            // Move the highlight DOWN to the last carousel without shifting the carousel list.
            focusCarouselAtIndex(
                dispatch,
                focusState.carouselIndex + 1,
                carousels,
                tileIndexPerCarousel,
            );
        } else {
            // Normal case: shift the carousel list up (increment verticalScrollIndex)
            // and move focus down together. The highlight stays fixed on screen.
            const newScrollIndex = Math.min(carousels.length - 1, verticalScrollIndex + 1);
            dispatch(setHomeScreenVerticalScrollAction(newScrollIndex));
            focusCarouselAtIndex(
                dispatch,
                focusState.carouselIndex + 1,
                carousels,
                tileIndexPerCarousel,
            );
            dispatch(checkAndLoadMoreCarousels());
        }
    }
};

export const onKeyOkHomeScreenOperations =
    () => async (dispatch: Function, getState: TGetState) => {
        const { homeScreen } = getState();

        if (homeScreen.navBarFocused) {
            const { navBarIndex, navItems } = homeScreen;

            if (navBarIndex < navItems.length) {
                // Dynamic nav item — route based on destination
                const { destination } = navItems[navBarIndex];
                switch (destination) {
                    case 'guide':
                        Log.i(TAG, 'Navigate to Guide screen from nav bar.');
                        KeyManager.getInstance().startKeyIgnoring();
                        dispatch(showGuideScreen());
                        break;
                    case 'myShows':
                        Log.i(TAG, 'Navigate to My Shows from nav bar.');
                        dispatch(showMyShowsScreen());
                        break;
                    case 'vodBrowse':
                        Log.i(TAG, 'Navigate to On Demand from nav bar.');
                        dispatch(showVodBrowseMainScreen());
                        break;
                    default:
                        Log.w(TAG, `Unhandled nav bar destination: ${destination}`);
                }
            } else {
                // Settings — always the last item
                Log.i(TAG, 'Navigate to Settings from nav bar.');
                dispatch(showMainSettingsScreen());
            }
            return;
        }

        const { focusState, carousels } = homeScreen;
        const carousel = carousels[focusState.carouselIndex];
        if (!carousel) return;

        const feedItem = carousel.feedItems?.feedItems?.[focusState.tileIndex];
        if (!feedItem) return;

        if (NetworkManager.isNetworkDisconnected()) {
            dispatch(showNoNetworkOverlay());
            return;
        }

        Log.i(
            TAG,
            `OK pressed on carousel: ${carousel.caption}, tile: ${feedItem.title}, offerType: ${feedItem.offerType}`,
        );

        KeyManager.getInstance().startKeyIgnoring();

        // Sports on Later - navigate to program screen
        const isFutureContent = feedItem.startTime && Date.parse(feedItem.startTime) > Date.now();
        if (isFutureContent) {
            Log.i(TAG, 'Tile is for future content, treating as Sports on Later');
            dispatch(
                programScreenTransition({
                    id: feedItem.offerId ?? '',
                    contentType: ContentType.OFFER_ID,
                    partnerId: feedItem.partnerId,
                }),
            );
            return;
        }

        // Series tiles → navigate to CollectionScreen
        if (feedItem.feedItemType === FeedItemType.Series && feedItem.feedItemId) {
            Log.i(TAG, `Navigating to collection screen for Series: ${feedItem.feedItemId}`);
            dispatch(
                collectionScreenTransition({
                    unifiedCollectionId: feedItem.feedItemId,
                    transitionScreen: TransitionScreenType.HOMESCREEN,
                    partnerId: feedItem.partnerId,
                }),
            );
            return;
        }

        // Linear content — always navigate to full play screen regardless of playability
        if (feedItem.offerType === OfferType.Live) {
            if (feedItem.stationId) {
                const channel = getSingleChannelInfo(feedItem.stationId);
                if (channel) {
                    Log.i(TAG, `Starting live playback for stationId: ${feedItem.stationId}`);
                    const livePlayable: IPlayable = {
                        channel,
                        playbackContentType: PlaybackContentType.LIVE,
                    };
                    dispatch(startVideoPlayback(livePlayable));
                    return;
                }
                Log.w(TAG, `Channel not found for stationId: ${feedItem.stationId}`);
            } else {
                Log.w(TAG, 'No stationId available for live tile');
            }
            // No channel resolved — stop key ignoring and do nothing further
            KeyManager.getInstance().stopKeyIgnoring();
            return;
        }

        // Non-live content — handle based on offerType
        switch (feedItem.offerType) {
            case OfferType.Recording: {
                Log.i(TAG, `Starting NDVR playback for recordingId: ${feedItem.feedItemId}`);
                dispatch(startNdvrPlayback(feedItem.feedItemId));
                break;
            }
            case OfferType.Vod:
            case OfferType.Socu: {
                Log.i(TAG, `Starting VOD/SoCu playback for offerId: ${feedItem.offerId}`);
                dispatch(startVodPlayback(feedItem));
                break;
            }
            default: {
                Log.w(TAG, `Unhandled offerType for OK press: ${feedItem.offerType}`);
                // Stop key ignoring since we don't have a defined action for this content type
                KeyManager.getInstance().stopKeyIgnoring();
                break;
            }
        }
    };

export const onKeyBackHomeScreenOperations = () => (dispatch: Function) => {
    if (isHomeScreenCcbSwitchEnabled()) {
        // Back on HomeScreen opens exit confirmation overlay
        dispatch(showExitConfirmationOverlayDialog());
    } else {
        // Back on HomeScreen opens sidebar navigation (same pattern as Guide)
        dispatch(showSidebarNavigationMenu());
    }
};

export const onKeyInfoHomeScreenOperations =
    () => async (dispatch: Function, getState: TGetState) => {
        const { homeScreen } = getState();

        if (homeScreen.navBarFocused) return;

        const { focusState, carousels, previewData } = homeScreen;
        const carousel = carousels[focusState.carouselIndex];
        const feedItem = carousel?.feedItems?.feedItems?.[focusState.tileIndex];

        if (!feedItem) return;

        if (NetworkManager.isNetworkDisconnected()) {
            dispatch(showNoNetworkOverlay());
            return;
        }

        Log.i(TAG, `Info key pressed on tile: ${feedItem.title}`);

        // Series tiles → open Infocard with feedItemId
        if (feedItem.feedItemType === FeedItemType.Series && feedItem.feedItemId) {
            Log.i(TAG, `Opening Infocard for Series: ${feedItem.feedItemId}`);
            const infoCard = InfoCardManager.getInstance();
            const kernel: UnifiedCollectionInfoCardKernel = {
                infoCardKernelType: InfoCardKernelType.UnifiedCollectionInfoCardKernel,
                unifiedCollectionId: feedItem.feedItemId,
                partnerId: feedItem.partnerId,
            };
            infoCard.setInfoCardKernel(kernel);
            infoCard.show();
            return;
        }

        // Other content → open RecordOverlay (requires offerId)
        if (!feedItem.offerId) {
            Log.w(TAG, 'No offerId available for record overlay');
            return;
        }

        const homeScreenInteractor = new HomeScreenInteractor();
        const offerPreviewForRecordOverlay: any =
            (await homeScreenInteractor.getOfferPreviewForRecordOverlay(feedItem)) ?? previewData;

        const recordingSharedPreferenceEditor = DeviceModel.getSharedPreferences(
            SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
        ).edit();
        recordingSharedPreferenceEditor.putString(
            SharedPrefConstants.RECORDING_OFFER_ID,
            feedItem.offerId,
        );
        const contentId = offerPreviewForRecordOverlay?.contentPreview?.contentId;
        if (contentId) {
            recordingSharedPreferenceEditor.putString(
                SharedPrefConstants.RECORDING_CONTENT_ID,
                contentId,
            );
        }
        recordingSharedPreferenceEditor.commit();

        dispatch(
            showRecordOverlay({
                feedItem,
                offerPreview: offerPreviewForRecordOverlay,
                showMetaData: true,
            }),
        );
    };
